/**
 * An operator's read-only view of this dashboard as one member (task 394).
 *
 * The admin console opens `/impersonate#<refresh JWT>` in a new tab. The token
 * is Descope's own impersonation of the member, minted by the admin API with a
 * 30-minute lifetime and an `hhImpersonation` claim naming the audit row.
 *
 * Three properties this module exists for:
 *
 * - **The fragment is gone at once.** `bootImpersonation` runs in `main.tsx`
 *   before React renders, and replaces the URL before it does anything else.
 *   A fragment never reaches CloudFront or S3 logs and never appears in a
 *   `Referer`; the expiry bounds what a copied URL is worth.
 * - **The session is tab-local.** It lives in this tab's `sessionStorage`, and
 *   the token layer is pointed there before anything reads a token, so an
 *   operator's own dashboard session in `localStorage` is never read, replaced
 *   or signed out, and no other tab picks the impersonation up.
 * - **It ends as an ended page, not a login screen.** A login screen in this
 *   tab would invite the operator to sign in as themselves in a tab whose
 *   storage says it is someone else's.
 *
 * The API is what makes the view read-only; this only keeps the operator from
 * clicking into its 403s (`useCurrentRole`).
 */

import { IMPERSONATION_CLAIM } from "@headerhawk/core/roles";
import {
  REFRESH_TOKEN_KEY,
  decodeJwtPayload,
  SESSION_TOKEN_KEY,
  logoutSession,
  notifySessionExpired,
  setTokenStorage,
  type DescopeProject,
} from "@/lib/auth/token-manager";

/** Where this tab's impersonation is described, in `sessionStorage`. */
export const IMPERSONATION_STATE_KEY = "headerhawk:impersonation";

/** The path the admin console opens. */
export const IMPERSONATE_PATH = "/impersonate";

/** Where the tab lands once the session is in place. */
const LANDING_PATH = "/dashboard";

export interface ImpersonationState {
  /** The `admin_audit_log` row that recorded this session. */
  auditId: string | null;
  operatorEmail: string | null;
  /** ISO 8601. */
  expiresAt: string | null;
  /** Why the session is over, once it is. */
  ended: "expired" | "ended" | "invalid" | null;
}

function tabStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * What a refresh JWT from the admin API says about its session, or null when
 * it is not one: a refresh token (`drn: "DSR"`) with an actor and our claim.
 */
export function readImpersonationToken(token: string): {
  auditId: string;
  operatorEmail: string | null;
  expiresAt: string;
} | null {
  const payload = decodeJwtPayload(token);
  if (!payload || payload["drn"] !== "DSR") return null;
  if (typeof payload["act"] !== "object" || payload["act"] === null) {
    return null;
  }
  const claim = payload[IMPERSONATION_CLAIM];
  if (typeof claim !== "object" || claim === null) return null;
  const { auditId, operatorEmail, expiresAt } = claim as Record<
    string,
    unknown
  >;
  if (typeof auditId !== "string" || typeof expiresAt !== "string") {
    return null;
  }
  return {
    auditId,
    operatorEmail: typeof operatorEmail === "string" ? operatorEmail : null,
    expiresAt,
  };
}

/** This tab's impersonation, or null in an ordinary tab. */
export function readImpersonationState(): ImpersonationState | null {
  const raw = tabStorage()?.getItem(IMPERSONATION_STATE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return {
      auditId: null,
      operatorEmail: null,
      expiresAt: null,
      ended: "invalid",
    };
  }
}

/**
 * Whether this tab is, or was, an operator's view of a member. Fixed for the
 * life of the page once `bootImpersonation` has run, so it is read once rather
 * than on every render and every persisted-store access.
 */
let impersonationTab: boolean | null = null;

export function isImpersonationTab(): boolean {
  impersonationTab ??= readImpersonationState() !== null;
  return impersonationTab;
}

function writeState(state: ImpersonationState): void {
  tabStorage()?.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(state));
}

/**
 * Take the session out of the URL, if this is `/impersonate`, and point the
 * token layer at this tab's storage if this tab is an impersonation. Call once
 * in `main.tsx`, before React renders and before analytics starts.
 */
export function bootImpersonation(
  location: Pick<Location, "pathname" | "hash"> = window.location,
  history: Pick<History, "replaceState"> = window.history,
): void {
  const storage = tabStorage();
  if (location.pathname.replace(/\/+$/, "") === IMPERSONATE_PATH && storage) {
    const fragment = location.hash.replace(/^#/, "");
    // First, before anything can read or log the URL.
    history.replaceState(null, "", IMPERSONATE_PATH);
    let token = "";
    try {
      token = decodeURIComponent(fragment);
    } catch {
      // A truncated or altered link: treated as carrying no session.
    }

    const session = token ? readImpersonationToken(token) : null;
    storage.removeItem(SESSION_TOKEN_KEY);
    if (session && Date.parse(session.expiresAt) > Date.now()) {
      storage.setItem(REFRESH_TOKEN_KEY, token);
      writeState({ ...session, ended: null });
      history.replaceState(null, "", LANDING_PATH);
    } else {
      storage.removeItem(REFRESH_TOKEN_KEY);
      writeState({
        auditId: session?.auditId ?? null,
        operatorEmail: session?.operatorEmail ?? null,
        expiresAt: session?.expiresAt ?? null,
        ended: session ? "expired" : "invalid",
      });
    }
  }

  impersonationTab = readImpersonationState() !== null;
  if (storage && impersonationTab) {
    setTokenStorage(storage);
  }
}

/** Record that the session is over, so a reload shows the ended page too. */
export function markImpersonationEnded(
  reason: NonNullable<ImpersonationState["ended"]>,
): void {
  const state = readImpersonationState();
  if (!state || state.ended) return;
  writeState({ ...state, ended: reason });
}

/**
 * End the session now: revoke the refresh token at Descope, forget it, and
 * tell the auth layer. The tab then shows the ended page.
 */
export async function endImpersonation(
  project: DescopeProject,
  reason: "expired" | "ended",
): Promise<void> {
  // `logoutSession` revokes the refresh token it finds, which is this tab's
  // only: the token layer was pointed at `sessionStorage` at boot.
  await logoutSession(project);
  markImpersonationEnded(reason);
  notifySessionExpired();
}
